import { FormEvent, useRef, useState } from "react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Banner } from "../ui/Banner";
import { Field } from "../ui/Field";
import { TextInput } from "../ui/TextInput";
import { useCreateInvite } from "./useCreateInvite";
import { useTranslation, apiErrorMessage } from "../i18n";
import type { Invite } from "../api/types";

// Expiry presets expressed as days → epoch-ms offset. The server caps every
// invite at 30 days (invites/service.ts MAX_EXPIRY_MS) and has no unbounded
// option, so "Never" is not offered — it would silently become 30 days
// (bugloop L1 #3).
const EXPIRY_PRESETS = [
  { labelKey: "invite.expiry1Day", days: 1 },
  { labelKey: "invite.expiry7Days", days: 7 },
  { labelKey: "invite.expiry30Days", days: 30 },
] as const;

type Role = "owner" | "member" | "viewer";
type ExpiryDays = 1 | 7 | 30;

function inviteUrl(invite: Invite): string {
  const token = invite.token;
  return `${window.location.origin}/invite/${token}`;
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback: execCommand (works in some environments without clipboard permission)
    try {
      const el = document.createElement("textarea");
      el.value = text;
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      return true;
    } catch {
      return false;
    }
  }
}

interface InviteModalProps {
  readonly slug: string;
  readonly open: boolean;
  readonly onClose: () => void;
}

export function InviteModal({ slug, open, onClose }: InviteModalProps) {
  const { t } = useTranslation("workspace");
  const { t: tErr } = useTranslation("errors");
  const [role, setRole] = useState<Role>("member");
  const [email, setEmail] = useState("");
  const [expiryDays, setExpiryDays] = useState<ExpiryDays>(7);
  const [createdInvite, setCreatedInvite] = useState<Invite | null>(null);
  const [copied, setCopied] = useState(false);
  const urlInputRef = useRef<HTMLInputElement>(null);

  const create = useCreateInvite(slug);

  function handleClose() {
    // Reset state when closing
    setRole("member");
    setEmail("");
    setExpiryDays(7);
    setCreatedInvite(null);
    setCopied(false);
    create.reset();
    onClose();
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const expiresAt =
      expiryDays !== null
        ? Date.now() + expiryDays * 86400_000
        : undefined;

    const trimmed = email.trim();
    create.mutate(
      { role, expiresAt, email: trimmed ? trimmed : undefined },
      {
        onSuccess: (inv) => {
          setCreatedInvite(inv);
        },
      },
    );
  }

  async function handleCopy() {
    if (!createdInvite) return;
    const url = inviteUrl(createdInvite);
    const ok = await copyToClipboard(url);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  const url = createdInvite ? inviteUrl(createdInvite) : "";

  return (
    <Modal open={open} onClose={handleClose} title={t("invite.title")}>
      {!createdInvite ? (
        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          {create.error ? (
            <Banner kind="error">{apiErrorMessage(create.error, tErr)}</Banner>
          ) : null}

          {/* Recipient email (optional) — when set, the server emails the link */}
          <Field label={t("invite.emailLabel")}>
            {({ id, ...ariaProps }) => (
              <TextInput
                id={id}
                type="email"
                autoComplete="off"
                placeholder={t("invite.emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                {...ariaProps}
              />
            )}
          </Field>

          {/* Role */}
          <Field label={t("invite.roleLabel")}>
            {({ id, ...ariaProps }) => (
              <select
                id={id}
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
                className="block w-full rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-100
                  focus:outline-none focus:ring-2 focus:ring-arch-500 focus:ring-offset-2 focus:ring-offset-slate-950"
                {...ariaProps}
              >
                <option value="viewer">{t("invite.roleViewer")}</option>
                <option value="member">{t("invite.roleMember")}</option>
                <option value="owner">{t("invite.roleOwner")}</option>
              </select>
            )}
          </Field>

          {/* Expiry */}
          <fieldset>
            <legend className="mb-2 text-sm font-medium text-slate-200">
              {t("invite.expiresIn")}
            </legend>
            <div className="flex flex-wrap gap-2">
              {EXPIRY_PRESETS.map((p) => (
                <label
                  key={String(p.days)}
                  className="flex cursor-pointer items-center gap-1.5"
                >
                  <input
                    type="radio"
                    name="expiry"
                    className="accent-arch-500"
                    checked={expiryDays === p.days}
                    onChange={() => setExpiryDays(p.days as ExpiryDays)}
                  />
                  <span className="text-sm text-slate-300">{t(p.labelKey)}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={handleClose} type="button">
              {t("invite.cancel")}
            </Button>
            <Button variant="primary" type="submit" disabled={create.isPending}>
              {create.isPending ? t("invite.creating") : t("invite.create")}
            </Button>
          </div>
        </form>
      ) : (
        <div className="space-y-4">
          <Banner kind="success">
            {createdInvite.email
              ? t("invite.createdEmailed", { email: createdInvite.email })
              : t("invite.created")}
          </Banner>

          <div>
            <p className="mb-1.5 text-sm font-medium text-slate-200">{t("invite.linkLabel")}</p>
            <div className="flex gap-2">
              <TextInput
                ref={urlInputRef}
                readOnly
                value={url}
                onFocus={(e) => e.target.select()}
                className="flex-1 font-mono text-xs"
              />
              <Button variant="secondary" onClick={handleCopy} size="md">
                {copied ? t("invite.copied") : t("invite.copy")}
              </Button>
            </div>
            <p className="mt-1.5 text-xs text-slate-400">
              {t("invite.roleSummary")}{" "}
              <span className="capitalize text-slate-300">{t(`members.roles.${createdInvite.role}`)}</span>
              {createdInvite.expiresAt ? (
                <>
                  {t("invite.expiresSummary")}
                  {new Date(createdInvite.expiresAt).toLocaleDateString()}
                </>
              ) : (
                t("invite.noExpiry")
              )}
            </p>
          </div>

          <div className="flex justify-end pt-1">
            <Button variant="primary" onClick={handleClose}>
              {t("invite.done")}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
